<script setup>
// build-force: 1776165600
import { h, ref, computed, onMounted, onBeforeUnmount, provide } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import LabelLeaf from './LabelLeaf.vue';
import { useRoute } from 'vue-router';
import SzCollapsedAccountSwitcher from 'dashboard/components/widgets/conversation/solarz/SzCollapsedAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { szSidebarCounts, fetchSzSidebarCounts } from './szSidebarCountsState';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const store = useStore();
const route = useRoute();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

// Collapsible sidebar state
const sidebarCollapsed = ref(false);
provide('sidebarCollapsed', sidebarCollapsed);

const toggleSidebarCollapse = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

// Auto-collapse when SZ Intelligence opens, expand when it closes
const onIntelligenceToggle = (e) => {
  if (e.detail && e.detail.open !== undefined) {
    sidebarCollapsed.value = e.detail.open;
  }
};
onMounted(() => {
  window.addEventListener('sz-sidebar-collapse', onIntelligenceToggle);
});
onBeforeUnmount(() => {
  window.removeEventListener('sz-sidebar-collapse', onIntelligenceToggle);
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const currentAccountId = useMapGetter('getCurrentAccountId');

// SZ: contadores na sidebar (Figma) — estado reativo compartilhado entre
// Sidebar e ChatList (banner de filtro ativo) via module ES.
// Refresh a cada 60s.
let szSidebarCountsTimer = null;
const refreshSzSidebarCounts = () => fetchSzSidebarCounts(currentAccountId.value);

// SZ: inboxes Channel::Api evogo2 desconectadas (mesma fonte do toaster top-right).
// Chatwoot não seta reauthorization_required para Channel::Api, então precisamos
// consultar o endpoint custom /sz/evogo2/account/:id/disconnected.
const szEvogo2DisconnectedIds = ref(new Set());
let szEvogo2Timer = null;
async function refreshSzEvogo2Disconnected() {
  const accountId = currentAccountId.value;
  if (!accountId) return;
  const token = store.state?.auth?.currentUser?.access_token;
  if (!token) return;
  try {
    const r = await fetch(`/sz/evogo2/account/${accountId}/disconnected`, {
      headers: { api_access_token: token },
    });
    if (!r.ok) return;
    const j = await r.json();
    const ids = new Set();
    (j.offline || []).forEach(i => { if (i && i.id) ids.add(i.id); });
    szEvogo2DisconnectedIds.value = ids;
  } catch (e) { /* noop — não derrubar UI por erro de rede */ }
}

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  refreshSzSidebarCounts();
  szSidebarCountsTimer = setInterval(refreshSzSidebarCounts, 60000);
  refreshSzEvogo2Disconnected();
  szEvogo2Timer = setInterval(refreshSzEvogo2Disconnected, 60000);
});

onBeforeUnmount(() => {
  if (szSidebarCountsTimer) clearInterval(szSidebarCountsTimer);
  if (szEvogo2Timer) clearInterval(szEvogo2Timer);
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  return [
    {
      name: 'Reports',
      label: 'Dashboard',
      icon: 'i-lucide-chart-spline',
      to: accountScopedRoute('account_overview_reports'),
      activeOn: [
        'account_overview_reports',
        'conversation_reports',
        'agent_reports', 'agent_reports_index', 'agent_reports_show',
        'label_reports', 'label_reports_index',
        'inbox_reports', 'inbox_reports_index', 'inbox_reports_show',
        'team_reports', 'team_reports_index', 'team_reports_show',
        'csat_reports', 'sla_reports', 'bot_reports',
      ],
    },
    {
      name: 'Chamadas',
      label: 'Chamadas',
      icon: 'i-lucide-phone',
      to: accountScopedRoute('sz_chamadas'),
      activeOn: ['sz_chamadas'],
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
            count: szSidebarCounts.value.teams[team.id] || 0,
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            count: szSidebarCounts.value.inboxes[inbox.id] || 0,
            disconnected:
              inbox.reauthorization_required === true ||
              szEvogo2DisconnectedIds.value.has(inbox.id),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                count: leafProps.count,
                disconnected: leafProps.disconnected,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: [...labels.value]
            // SZ: mostra apenas etiquetas com conversas vinculadas (count > 0)
            .filter(
              label => (szSidebarCounts.value.labels[label.title] || 0) > 0
            )
            // SZ: ordena alfabeticamente (case-insensitive, com locale pt-BR)
            .sort((a, b) =>
              a.title.localeCompare(b.title, 'pt-BR', { sensitivity: 'base' })
            )
            .map(label => {
              // SZ: toggle — se a etiqueta JA esta filtrada, clicar nela remove
              // o filtro (volta pra rota base 'home'). Aceita as duas rotas:
              //   label_conversations         = /label/:label (so lista)
              //   conversations_through_label = /label/:label/conversations/:id
              const routeLabel = route.params.label;
              const isCurrentFilter =
                (route.name === 'label_conversations' ||
                  route.name === 'conversations_through_label') &&
                (routeLabel === label.title ||
                  decodeURIComponent(routeLabel || '') === label.title);
              return {
                name: `${label.title}-${label.id}`,
                label: label.title,
                component: leafProps =>
                  h(LabelLeaf, {
                    label: leafProps.label,
                    // SZ: leafProps.active vem do SidebarGroup baseado no `to`
                    // (que comparamos vs rota atual). Como o toggle muda o `to`
                    // pra home quando filtrado, o leafProps.active fica false
                    // mesmo o usuario estando NA tela da etiqueta. Aqui forco
                    // active=true baseado na rota real pra o visual ficar
                    // correto.
                    active: isCurrentFilter,
                    count: leafProps.count,
                    color: label.color,
                  }),
                to: isCurrentFilter
                  ? accountScopedRoute('home')
                  : accountScopedRoute('label_conversations', {
                      label: label.title,
                    }),
                count: szSidebarCounts.value.labels[label.title] || 0,
              };
            }),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1, search: undefined }),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
      ],
    },

  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-white dark:bg-[#1D1E21] flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 md:static md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:-translate-x-0 sz-sidebar-transition"
    :class="[
      sidebarCollapsed ? 'sz-sidebar-collapsed w-[56px] basis-[56px]' : 'w-[230px] basis-[230px]',
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
      },
    ]"
  >
    <section class="sz-sidebar-header">
      <!-- Collapsed: avatar circular -->
      <div v-if="sidebarCollapsed" class="flex items-center justify-center px-2 h-8">
        <SzCollapsedAccountSwitcher mode="collapsed" />
      </div>
      <!-- Expanded: mesmo componente Sz com pill horizontal (design Figma unificado) -->
      <div v-else class="flex items-center px-2 min-w-0">
        <SzCollapsedAccountSwitcher mode="expanded" class="flex-grow min-w-0" />
      </div>
      <div class="mx-2 border-b border-n-weak" />
    </section>

    <nav class="grid overflow-y-scroll flex-grow gap-2 px-2 pt-2 pb-5 no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <!-- Profile section removed - moved to topbar -->
    <!-- Toggle button on sidebar edge -->
    <button
      class="sz-sidebar-edge-toggle"
      :title="sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'"
      @click="toggleSidebarCollapse"
    >
      <img :src="'/static/img/sz-expand-icon.svg'" alt="" class="sz-edge-toggle-icon" :class="{ 'sz-edge-toggle-rotated': !sidebarCollapsed }" style="width:15px;height:15px;" />
    </button>
  </aside>
</template>
